// React
import { ElementType, Suspense } from "react";

// Third-Party
import { Box, useTheme } from "@mui/material";
import {
  CircleAlert as ICircleAlertIcon,
  FileChartColumn as IconAnalytics,
  Landmark as IconBank,
  List as IconCatalog,
  Cpu as IconCpu,
  CreditCard as IconCreditCard,
  Ruler as IconDimension,
  ScanText as IconExtraction,
  Folder as IconFolder,
  House as IconHouse,
  Plug2 as IconIntegration,
  BanknoteArrowUp as IconPayable,
  BanknoteArrowDown as IconReceivable,
  Settings as IconSettings,
  Package as IconSupplier,
  ListChecks as IconTasks,
  TrendingUp as IconTrendingUp,
  Users as IconUsers,
  Workflow as IconWorkflow,
  Waypoints as IconWorkflowTemplates,
  ShoppingCart,
} from "lucide-react";
import { useLocation } from "react-router";

// Internal
import { getActive } from "@components/nav-section";
import useEnabledModules from "@hooks/useEnabledModules";
import usePermissions from "@hooks/usePermissions";
import useDefaultQuery from "@pages/Documents/Finance/hooks/useDefaultQuery";
import { RootState, useSelector } from "@redux/store";
import {
  PATH_CATALOG,
  PATH_CONFIGURATION,
  PATH_DASHBOARD,
  PATH_DIMENSION,
  PATH_EXPENSE,
  PATH_PAYABLES,
  PATH_PAYMENTS,
  PATH_PROCUREMENT,
  PATH_RECEIVABLES,
  PATH_REPORT,
  PATH_TASKS,
  PATH_TRAINING,
} from "@routes/paths";
import {
  getStandardReadPermissions,
  isStandardizationOn,
} from "@utils/standardPermissions";
import { ENABLE_BANK_RECONCILIATION_V2 } from "src/constants/settings";
import { useBanner } from "src/contexts/BannerContext";

// Types
import { Permissions } from "src/@types/permissions";

export const Loadable = (Component: ElementType) => (props: any) => {
  return (
    <Suspense fallback={<></>}>
      <Component {...props} />
    </Suspense>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default () => {
  const isLLMExtraction = useSelector(
    (state: RootState) => state.organization.org.isLLMExtraction,
  );
  const hideDocumentlistingScreen = useSelector(
    (state: RootState) => state.organization.org.hideDocumentlistingScreen,
  );
  const enableWorkflowTemplateExecution = useSelector(
    (state: RootState) =>
      state.organization.org.enableWorkflowTemplateExecution,
  );

  const { permissionHandle } = usePermissions();
  const { moduleHandle } = useEnabledModules();
  const { path: documentPath } = useDefaultQuery();
  const { hasUnClassifiedDoc } = useBanner();

  const { pathname } = useLocation();
  const { palette } = useTheme();

  const getIconColor = (path: string, pathname: string, isPartial = false) =>
    getActive(path, pathname, isPartial)
      ? palette.primary.main
      : palette.text.label;

  const unclassifiedDot = hasUnClassifiedDoc ? (
    <Box
      sx={{
        display: "flex",
        color: palette.error.dark,
        flexShrink: 0,
        animation: "pulse 1.4s infinite",
        "@keyframes pulse": {
          "0%": {
            transform: "scale(1)",
            opacity: 1,
          },
          "50%": {
            transform: "scale(1.35)",
            opacity: 0.75,
          },
          "100%": {
            transform: "scale(1)",
            opacity: 1,
          },
        },
      }}
    >
      <ICircleAlertIcon size={16} strokeWidth="2px" />
    </Box>
  ) : undefined;

  // enableStandardization: AP/AR entries lock on their class View/Update
  // permissions (Documents_Read/Write when off); purchase orders move to Procurement.
  const isStandard = isStandardizationOn();
  const classPermissions = (
    accountClass: string,
    ...documentClasses: string[]
  ) =>
    documentClasses.flatMap((documentClass) =>
      getStandardReadPermissions(accountClass, documentClass),
    );
  const purchaseOrdersNav = {
    title: "purchase_orders",
    path: isStandard
      ? PATH_PROCUREMENT.general.purchaseOrder.root
      : PATH_PAYABLES.general.purchaseOrder.root,
    locked: !permissionHandle(
      classPermissions("AccountPayable", "PurchaseOrder"),
    ),
    moduleLocked: !moduleHandle([isStandard ? "Procurement" : "Document"]),
    module: isStandard ? "Procurement" : "Document",
    role: true,
  };

  const navConfig = [
    {
      show: true,
      items: [
        {
          title: "_dashboard",
          path: PATH_DASHBOARD.general.app,
          icon: (
            <IconHouse
              color={getIconColor(PATH_DASHBOARD.general.app, pathname)}
            />
          ),
          module: "Dashboard",
          role: true,
        },
        ...(enableWorkflowTemplateExecution
          ? [
              {
                title: "tasks",
                path: PATH_TASKS.root,
                icon: (
                  <IconTasks color={getIconColor(PATH_TASKS.root, pathname)} />
                ),
                role: true,
              },
            ]
          : []),
        ...(!hideDocumentlistingScreen
          ? [
              {
                title: "documents",
                path: documentPath,
                icon: (
                  <IconFolder
                    color={getIconColor(
                      PATH_DASHBOARD.general.documents.root,
                      pathname,
                    )}
                  />
                ),
                locked: !permissionHandle([
                  Permissions.Documents_Read,
                  Permissions.Documents_Write,
                ]),
                moduleLocked: !moduleHandle(["Document"]),
                module: "Document",
                role: true,
              },
            ]
          : []),
        {
          title: "payables",
          path: "/payables",
          icon: (
            <IconPayable color={getIconColor(PATH_PAYABLES.root, pathname)} />
          ),
          locked: !permissionHandle([
            Permissions.Documents_Read,
            Permissions.Documents_Write,
            ...classPermissions("AccountPayable", "Invoice", "DeliveryNote"),
          ]),
          moduleLocked: !moduleHandle(["Document"]),
          module: "Document",
          role: true,
          children: [
            {
              title: "purchase_invoices",
              path: PATH_PAYABLES.general.purchaseInvoice.root,
              locked: !permissionHandle(
                classPermissions("AccountPayable", "Invoice"),
              ),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
            },
            ...(isStandard ? [] : [purchaseOrdersNav]),
            {
              title: "purchase_delivery_note",
              path: PATH_PAYABLES.general.purchaseDeliverynote.root,
              locked: !permissionHandle(
                classPermissions("AccountPayable", "DeliveryNote"),
              ),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
            },
            {
              title: "unclassified",
              path: PATH_PAYABLES.general.unclassified,
              locked: !permissionHandle([
                Permissions.Documents_Read,
                Permissions.Documents_Write,
              ]),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
              info: unclassifiedDot,
            },
          ],
        },
        {
          title: "receivables",
          path: "/receivables",
          icon: (
            <IconReceivable
              color={getIconColor(PATH_RECEIVABLES.root, pathname, true)}
            />
          ),
          locked: !permissionHandle([
            Permissions.Documents_Read,
            Permissions.Documents_Write,
            ...classPermissions(
              "AccountReceivable",
              "Invoice",
              "Order",
              "DeliveryNote",
            ),
          ]),
          moduleLocked: !moduleHandle(["Document"]),
          module: "Document",
          role: true,
          children: [
            {
              title: "sales_invoices",
              path: PATH_RECEIVABLES.general.saleInvoice.root,
              locked: !permissionHandle(
                classPermissions("AccountReceivable", "Invoice"),
              ),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
            },
            {
              title: "sales_orders",
              path: PATH_RECEIVABLES.general.saleOrder.root,
              locked: !permissionHandle(
                classPermissions("AccountReceivable", "Order"),
              ),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
            },
            {
              title: "sales_delivery_note",
              path: PATH_RECEIVABLES.general.saleDeliveryNote.root,
              locked: !permissionHandle(
                classPermissions("AccountReceivable", "DeliveryNote"),
              ),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
            },
            {
              title: "unclassified",
              path: PATH_RECEIVABLES.general.unclassified,
              locked: !permissionHandle(["Documents_Read", "Documents_Write"]),
              moduleLocked: !moduleHandle(["Document"]),
              module: "Document",
              role: true,
              info: unclassifiedDot,
            },
          ],
        },

        {
          title: "procurement",
          path: "/procurement",
          icon: (
            <ShoppingCart
              color={getIconColor(PATH_PROCUREMENT.root, pathname)}
            />
          ),
          locked: !moduleHandle(["Procurement"]),
          moduleLocked: !moduleHandle(["Procurement"]),
          module: "Procurement",
          role: true,
          children: [
            {
              title: "purchase_requisitions",
              path: PATH_PROCUREMENT.general.purchaseRequisition.general
                .purchaseRequisitionList,
              locked: !permissionHandle([
                Permissions.Procurement_PurchaseRequisition_View,
              ]),
              moduleLocked: !moduleHandle(["Procurement"]),
              module: "Procurement",
              role: true,
            },
            {
              title: "grn",
              path: PATH_PROCUREMENT.general.grn.general.grnList,
              locked: !permissionHandle([Permissions.Procurement_GoodsReceivedNote_View]),
              moduleLocked: !moduleHandle(["Procurement"]),
              module: "Procurement",
              role: true,
            },
            ...(isStandard ? [purchaseOrdersNav] : []),
          ],
        },
        // Configuration
        {
          title: "configuration",
          path: "/configuration",
          icon: (
            <IconSettings
              color={getIconColor(PATH_CONFIGURATION.root, pathname, true)}
            />
          ),
          role: true,
        },

        {
          title: "integration",
          path: PATH_DASHBOARD.general.integration,
          icon: (
            <IconIntegration
              color={getIconColor(PATH_DASHBOARD.general.integration, pathname)}
            />
          ),
          locked: !permissionHandle([
            "Integrations_Read",
            "Integrations_Write",
          ]),
          moduleLocked: !moduleHandle(["Connector"]),
          module: "Connector",
          role: true,
        },

        {
          title: "workflow",
          path: enableWorkflowTemplateExecution
            ? PATH_DASHBOARD.general.workflowTemplates
            : PATH_DASHBOARD.general.workflow,
          icon: enableWorkflowTemplateExecution ? (
            <IconWorkflowTemplates
              color={getIconColor(
                PATH_DASHBOARD.general.workflowTemplates,
                pathname,
              )}
            />
          ) : (
            <IconWorkflow
              color={getIconColor(PATH_DASHBOARD.general.workflow, pathname)}
            />
          ),
          locked: enableWorkflowTemplateExecution
            ? !permissionHandle([
                "WorkflowManagement_Read",
                "WorkflowManagement_Write",
              ])
            : !permissionHandle([
                "WorkflowManagement_Read",
                "WorkflowManagement_Write",
                "WorkflowApproval_Read",
                "WorkflowApproval_Write",
              ]),
          moduleLocked: !moduleHandle(["Workflow"]),
          module: "Workflow",
          role: true,
        },
        {
          title: "suppliers",
          path: PATH_DASHBOARD.general.suppliers,
          icon: (
            <IconSupplier
              color={getIconColor(PATH_DASHBOARD.general.suppliers, pathname)}
            />
          ),
          locked: !permissionHandle(["Suppliers_Read", "Suppliers_Write"]),
          moduleLocked: !moduleHandle(["Supplier"]),
          module: "Supplier",
          role: true,
        },

        {
          title: "Customers",
          path: PATH_DASHBOARD.general.customers,
          icon: (
            <IconUsers
              color={getIconColor(PATH_DASHBOARD.general.customers, pathname)}
            />
          ),
          locked: !permissionHandle(["Customers_Read", "Customers_Write"]),
          moduleLocked: !moduleHandle(["Customer"]),
          module: "Customer",
          role: true,
        },

        {
          title: "catalog",
          path: "/catalog",
          icon: (
            <IconCatalog
              color={getIconColor(PATH_CATALOG.root, pathname, true)}
            />
          ),
          role: true,
          children: [
            {
              title: "items",
              path: PATH_CATALOG.general.items,
              role: true,
              locked: !permissionHandle(["Items_Read", "Items_Write"]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
            {
              title: "taxes",
              path: PATH_CATALOG.general.taxes,
              role: true,
              locked: !permissionHandle(["Taxes_Read", "Taxes_Write"]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
            {
              title: "dimensions",
              path: PATH_DIMENSION.root,
              icon: (
                <IconDimension
                  color={getIconColor(PATH_DIMENSION.root, pathname)}
                />
              ),
              role: true,
              locked: !permissionHandle([Permissions.Catalog_Dimensions_View]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
            {
              title: "currencies",
              path: PATH_CATALOG.general.currencies,
              role: true,
              locked: !permissionHandle([Permissions.Catalog_Currencies_View]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
            {
              title: "payment_methods",
              path: PATH_CATALOG.general.paymentMethods,
              role: true,
              locked: !permissionHandle([
                Permissions.Catalog_PaymentMethods_View,
              ]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
            {
              title: "payment_terms",
              path: PATH_CATALOG.general.paymentTerms,
              role: true,
              locked: !permissionHandle([Permissions.Catalog_PaymentTerms_View]),
              moduleLocked: !moduleHandle(["Catalog"]),
              module: "Catalog",
              color: palette.text.label,
            },
          ],
        },

        {
          title: "bank",
          path: "/payments",
          icon: (
            <IconBank
              color={getIconColor(PATH_PAYMENTS.root, pathname, true)}
            />
          ),
          role: true,
          children: [
            {
              title: "bank_accounts",
              path: PATH_PAYMENTS.general.bankAccounts,
              locked: !permissionHandle([
                "PaymentGateway_Read",
                "PaymentGateway_Write",
              ]),
              moduleLocked: !moduleHandle(["Payment"]),
              module: "Payment",
              role: true,
              color: palette.text.label,
            },

            {
              title: "invoice_payment",
              path: PATH_PAYMENTS.general.invoicePayments,
              locked: !permissionHandle([
                "InvoicePayments_Read",
                "InvoicePayments_Write",
              ]),
              moduleLocked: !moduleHandle(["Payment"]),
              module: "Payment",
              role: true,
              color: palette.text.label,
            },
            {
              title: "bank_transactions",
              path: PATH_PAYMENTS.general.invoiceTransactions,
              locked: !permissionHandle([
                "BankTransactions_Read",
                "BankTransactions_Write",
              ]),
              moduleLocked: !moduleHandle(["Payment"]),
              module: "Payment",
              role: true,
              color: palette.text.label,
            },
            {
              title: "payment_schedules",
              path: PATH_PAYMENTS.general.paymentSchedules,
              locked: !permissionHandle([
                "PaymentSchedule_Read",
                "PaymentSchedule_Write",
              ]),
              moduleLocked: !moduleHandle(["Payment"]),
              module: "Payment",
              role: true,
              color: palette.text.label,
            },
            {
              title: "bank_reconciliation",
              path: ENABLE_BANK_RECONCILIATION_V2
                ? PATH_PAYMENTS.general.bankReconciliationV2
                : PATH_PAYMENTS.general.bankReconciliation,
              locked: !permissionHandle([
                "BankReconciliation_Read",
                "BankReconciliation_Write",
              ]),
              moduleLocked: !moduleHandle(["Payment"]),
              module: "Payment",
              role: true,
              color: palette.text.label,
            },
          ],
        },

        {
          title: "expense",
          path: "/expense",
          icon: (
            <IconCreditCard
              color={getIconColor(PATH_EXPENSE.root, pathname, true)}
            />
          ),
          role: true,
          children: [
            {
              title: "employee",
              path: PATH_EXPENSE.general.employees,
              locked: !permissionHandle(["Employee_Read", "Employee_Write"]),
              moduleLocked: !moduleHandle(["Expense"]),
              module: "Expense",
              role: true,
              color: palette.text.label,
            },
            {
              title: "expenses",
              path: PATH_EXPENSE.general.expenses,
              locked: !permissionHandle(["Expenses_Read", "Expenses_Write"]),
              moduleLocked: !moduleHandle(["Expense"]),
              module: "Expense",
              role: true,
              color: palette.text.label,
            },
          ],
        },

        {
          title: "analytics",
          path: PATH_DASHBOARD.general.analyticsV3,
          icon: (
            <IconTrendingUp
              color={getIconColor(PATH_DASHBOARD.general.analyticsV3, pathname)}
            />
          ),
          locked: !permissionHandle(["Analytics_Read"]),
          moduleLocked: !moduleHandle(["Analytics"]),
          module: "Analytics",
          role: true,
        },

        {
          title: "training",
          path: "/training",
          icon: (
            <IconCpu color={getIconColor(PATH_TRAINING.root, pathname, true)} />
          ),
          role: true,
          children: [
            {
              title: "self_training",
              path: PATH_TRAINING.general.selfTraining,
              icon: (
                <IconCpu
                  color={getIconColor(
                    PATH_TRAINING.general.selfTraining,
                    pathname,
                  )}
                />
              ),
              locked: !permissionHandle(["SelfTraining_Read"]),
              moduleLocked: !moduleHandle(["SelfTraining"]),
              module: "SelfTraining",
              role: true,
            },
            ...(isLLMExtraction
              ? [
                  {
                    title: "extraction_rules",
                    path: PATH_TRAINING.general.extractionRules,
                    icon: (
                      <IconExtraction
                        color={getIconColor(
                          PATH_TRAINING.general.extractionRules,
                          pathname,
                        )}
                      />
                    ),
                    module: "ExtractionRules",
                    role: true,
                  },
                ]
              : []),
          ],
        },

        {
          title: "Reports",
          path: PATH_REPORT.root,
          icon: (
            <IconAnalytics color={getIconColor(PATH_REPORT.root, pathname)} />
          ),
          moduleLocked: !moduleHandle(["Report"]),
          module: "Report",
          locked: !permissionHandle(["Report_Read"]),
          role: true,
        },
      ],
    },
  ];
  return { navConfig };
};
